import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Bot, X } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const EMPTY = { question: "", answer: "", keywords: "", is_active: true };

export default function AdminFaqs() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/faqs").then((r) => setFaqs(r.data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing("new"); setForm(EMPTY); };
  const openEdit = (f) => { setEditing(f.id); setForm({ question: f.question, answer: f.answer, keywords: (f.keywords || []).join(", "), is_active: f.is_active }); };

  const save = async () => {
    if (!form.question.trim() || !form.answer.trim()) {
      toast.error("Question and answer are required");
      return;
    }
    const payload = {
      question: form.question.trim(),
      answer: form.answer.trim(),
      keywords: form.keywords.split(",").map((k) => k.trim()).filter(Boolean),
      is_active: form.is_active,
    };
    try {
      if (editing === "new") await api.post("/admin/faqs", payload);
      else await api.put(`/admin/faqs/${editing}`, payload);
      toast.success("FAQ saved — the chatbot will use it immediately");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    try {
      await api.delete(`/admin/faqs/${id}`);
      toast.success("FAQ deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl border border-[#D6E4DC] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-lime/50";
  const labelCls = "block text-xs font-bold text-brand-emerald uppercase tracking-wider mb-1.5";

  return (
    <div data-testid="admin-faqs-page">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-brand-emerald flex items-center gap-2">Chatbot FAQs <Bot className="w-7 h-7 text-brand-lime" /></h1>
          <p className="text-brand-muted text-sm mt-1">ProperBot answers these instantly before handing over to you.</p>
        </div>
        <button onClick={openNew} data-testid="faq-new-btn" className="px-5 py-2.5 rounded-full bg-brand-emerald text-white text-sm font-bold hover:bg-brand-lime transition-colors flex items-center gap-2">
          <Plus className="w-4 h-4" /> New FAQ
        </button>
      </div>

      {editing !== null && (
        <div className="bg-white rounded-2xl border border-brand-lime/50 shadow-lg p-6 mb-6 animate-pop-in" data-testid="faq-editor">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-brand-emerald">{editing === "new" ? "New FAQ" : "Edit FAQ"}</h2>
            <button onClick={() => setEditing(null)} className="p-1.5 rounded-lg text-brand-muted hover:bg-brand-mint" aria-label="Close editor"><X className="w-4 h-4" /></button>
          </div>
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Question *</label>
              <input data-testid="faq-question-input" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} placeholder="e.g. Do you clean on Sundays?" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Bot's Answer *</label>
              <textarea data-testid="faq-answer-input" value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} rows={4} placeholder="The exact answer the bot will send…" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Trigger Keywords (comma separated)</label>
              <input data-testid="faq-keywords-input" value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="sunday, weekend, available" className={inputCls} />
            </div>
            <label className="flex items-center gap-2 text-sm font-semibold text-brand-ink">
              <input type="checkbox" data-testid="faq-active-checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="accent-[#7AB800] w-4 h-4" /> Active (bot uses this answer)
            </label>
            <button onClick={save} data-testid="faq-save-btn" className="px-6 py-3 rounded-full bg-brand-emerald text-white text-sm font-bold hover:bg-brand-lime transition-colors">Save FAQ</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-10 h-10 border-4 border-brand-emerald border-t-brand-lime rounded-full animate-spin" /></div>
      ) : (
        <div className="space-y-3">
          {faqs.map((f) => (
            <div key={f.id} className="bg-white rounded-2xl border border-[#D6E4DC] p-5" data-testid={`faq-row-${f.id.slice(0, 8)}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1.5">
                    <p className="font-bold text-brand-ink">{f.question}</p>
                    {!f.is_active && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Inactive</span>}
                  </div>
                  <p className="text-sm text-brand-muted leading-relaxed">{f.answer}</p>
                  {f.keywords?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {f.keywords.map((k) => <span key={k} className="text-[10px] font-bold bg-brand-mint text-brand-emerald px-2 py-1 rounded-full">{k}</span>)}
                    </div>
                  )}
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button onClick={() => openEdit(f)} data-testid={`faq-edit-${f.id.slice(0, 8)}`} className="p-2 rounded-lg text-brand-muted hover:bg-brand-mint hover:text-brand-emerald" aria-label="Edit FAQ"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(f.id)} data-testid={`faq-delete-${f.id.slice(0, 8)}`} className="p-2 rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600" aria-label="Delete FAQ"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
